import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton, Badge } from '@mui/material';
import { ShoppingCart } from '@mui/icons-material';
import { useCart } from '../../contexts/CartContext';

interface HeaderProps {
  onCartClick?: () => void;
}

export const Header = ({ onCartClick }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { getTotalItems } = useCart();

  const toggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navLinks = [
    { name: 'Главная', path: '/' },
    { name: 'Каталог', path: '/catalog' },
  ];

  return (
    <>
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        background: 'rgba(10, 10, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        padding: '2rem 0',
        boxShadow: '0 2px 20px rgba(255, 0, 110, 0.3)',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Mobile menu toggle */}
          <div 
            className={`menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            style={{
              flexDirection: 'column',
              cursor: 'pointer',
              padding: '0.5rem',
              zIndex: 1001,
            }}
          >
            <span style={{
              width: '25px',
              height: '3px',
              background: 'linear-gradient(135deg, #b8306a, #6d3fb3, #3d6cc7)',
              margin: '3px 0',
              transition: '0.3s',
              borderRadius: '2px',
            }}></span>
            <span style={{
              width: '25px',
              height: '3px',
              background: 'linear-gradient(135deg, #b8306a, #6d3fb3, #3d6cc7)',
              margin: '3px 0',
              transition: '0.3s',
              borderRadius: '2px',
            }}></span>
            <span style={{
              width: '25px',
              height: '3px',
              background: 'linear-gradient(135deg, #b8306a, #6d3fb3, #3d6cc7)',
              margin: '3px 0',
              transition: '0.3s',
              borderRadius: '2px',
            }}></span>
          </div>

          {/* Logo */}
          <div 
            style={{
              fontFamily: 'Orbitron, sans-serif',
              fontSize: '2rem',
              fontWeight: 900,
              background: 'linear-gradient(135deg, #b8306a, #6d3fb3, #3d6cc7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(211, 52, 110, 0.3)',
              animation: 'neonFlicker 2s ease-in-out infinite, logoPulse 3s ease-in-out infinite',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              letterSpacing: '1px',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          >
            NAME STORE
          </div>

          {/* Desktop Navigation */}
          <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`} style={{
            display: 'flex',
            gap: '2rem',
            listStyle: 'none',
          }}>
            {navLinks.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.path}
                  className="nav-link"
                  style={{
                    fontFamily: 'Exo 2, sans-serif',
                    color: '#e0e0e0',
                    textDecoration: 'none',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    padding: '0.5rem 1rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.color = '#ffffff';
                    target.style.background = 'linear-gradient(135deg, #7b45c7, #9c27b0, #ba68c8)';
                    target.style.backgroundClip = 'text';
                    target.style.webkitBackgroundClip = 'text';
                    target.style.webkitTextFillColor = 'transparent';
                    target.style.textShadow = '0 0 15px rgba(123, 69, 199, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.color = '#e0e0e0';
                    target.style.background = 'transparent';
                    target.style.backgroundClip = 'unset';
                    target.style.webkitBackgroundClip = 'unset';
                    target.style.webkitTextFillColor = 'unset';
                    target.style.textShadow = 'none';
                  }}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Cart Button */}
          <IconButton
            onClick={onCartClick}
            sx={{
              color: '#e0e0e0',
              background: 'linear-gradient(135deg, rgba(123, 69, 199, 0.1), rgba(156, 39, 176, 0.1))',
              border: '1px solid rgba(123, 69, 199, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(123, 69, 199, 0.2), rgba(156, 39, 176, 0.2))',
                color: '#ffffff',
              },
            }}
          >
            <Badge badgeContent={getTotalItems()} color="primary">
              <ShoppingCart />
            </Badge>
          </IconButton>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      <div
        className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}
        style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          width: '100%',
          height: 'calc(100vh - 80px)',
          background: 'rgba(10, 10, 10, 0.98)',
          backdropFilter: 'blur(20px)',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          paddingTop: '2rem',
          gap: '2rem',
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s ease',
          zIndex: 999,
          display: 'none',
        }}
      >
        {navLinks.map((link) => (
          <li key={link.name} style={{ listStyle: 'none', width: '100%' }}>
            <Link
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                fontFamily: 'Exo 2, sans-serif',
                color: '#e0e0e0',
                textDecoration: 'none',
                padding: '1rem 2rem',
                textAlign: 'center',
                width: '100%',
                borderBottom: '1px solid rgba(109, 63, 179, 0.1)',
                display: 'block',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {link.name}
            </Link>
          </li>
        ))}
      </div>
    </>
  );
};
